import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EASE } from "./Reveal";
import { ZoomImage } from "./Lightbox";
import { Sparkles } from "./Sparkles";
import { SLIDES } from "./Carousel";

const HERO_SLIDES = SLIDES.slice(0, 5);
const AUTOPLAY_MS = 5000;
const variants = {
  enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.3 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0.3 }),
};

export const HeroSlider = ({ slides = HERO_SLIDES, parallaxStyle }) => {
  const [[index, dir], setState] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const n = slides.length;
  const go = useCallback((next, d) => setState([((next % n) + n) % n, d]), [n]);
  const prev = () => go(index - 1, -1);
  const next = useCallback(() => go(index + 1, 1), [go, index]);

  useEffect(() => { slides.forEach((s) => { const i = new Image(); i.src = s.src; }); }, [slides]);
  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused, next]);

  const slide = slides[index];

  return (
    <div
      className="group absolute inset-0" data-testid="hero-slider" role="region" aria-roledescription="carousel" aria-label="Featured lab grown diamonds"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onKeyDown={(e) => { if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); }} tabIndex={0}
    >
      <motion.div style={parallaxStyle} className="absolute -inset-4">
      <AnimatePresence initial={false} custom={dir} mode="popLayout">
        <motion.div
          key={index} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
          transition={{ x: { duration: 0.8, ease: EASE }, opacity: { duration: 0.5 } }}
          drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.12}
          onDragEnd={(_, info) => { if (info.offset.x < -60 || info.velocity.x < -400) next(); else if (info.offset.x > 60 || info.velocity.x > 400) prev(); }}
          className="absolute inset-0" data-testid={`hero-slide-${index}`} aria-roledescription="slide" aria-label={`${index + 1} of ${n}: ${slide.caption}`}
        >
          <ZoomImage src={slide.src} alt={slide.alt} draggable={false} className={`h-full w-full object-cover object-center ${slide.src === "/img/hero.jpg" ? "lg:object-contain" : ""}`} />
        </motion.div>
      </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-night via-night/20 to-night/30 lg:opacity-50" />
      <Sparkles count={16} />

      <motion.div
        key={`card-${index}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
        className="absolute bottom-20 right-4 sm:bottom-24 sm:right-8 lg:bottom-4 lg:right-auto lg:left-4 bg-night/80 backdrop-blur border border-gold/40 px-4 py-3 sm:px-5 sm:py-4 pointer-events-none"
        data-testid="hero-slide-card"
      >
        <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-gold">{slide.eyebrow}</p>
        <p className="font-display text-xl sm:text-2xl text-ivory mt-1">{slide.caption}</p>
      </motion.div>

      <button data-testid="hero-slider-prev" onClick={prev} aria-label="Previous image" className="carousel-arrow left-3 !h-10 !w-10"><ChevronLeft size={20} strokeWidth={1.5} /></button>
      <button data-testid="hero-slider-next" onClick={next} aria-label="Next image" className="carousel-arrow right-3 !h-10 !w-10"><ChevronRight size={20} strokeWidth={1.5} /></button>

      <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 lg:bottom-4 flex items-center gap-2" role="tablist" aria-label="Choose featured image" data-testid="hero-slider-dots">
        {slides.map((s, i) => (
          <button key={s.src} role="tab" aria-selected={i === index} aria-label={`Go to slide ${i + 1}`} data-testid={`hero-slider-dot-${i}`} onClick={() => go(i, i > index ? 1 : -1)} className="p-1.5 -m-1">
            <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-500 ${i === index ? "w-6 bg-saffron" : "w-1.5 bg-ivory/40 hover:bg-gold"}`} />
          </button>
        ))}
      </div>
    </div>
  );
};
